import { useEffect, useMemo, useRef, useState } from "react";
import { contractsService, type ContractDetails } from "../services/contractsService";
import {
  optionChainsV2Service,
  type OptionChainContract,
} from "../services/optionChainsV2Service";
import type { ProductSearchItem } from "../types/productV3";
import {
  flattenStrategyPositions,
  computeHoldingsSummary,
  type StrategyPositionInput,
  type StrategyFlatPosition,
  type StrategyHoldingsSummary,
} from "../utils/strategyFlatPositions";

export interface EnrichedHoldingRow {
  conId: number;
  ticker: string;
  name: string;
  exchange: string;
  currency: string;
  netWeight: number;
  direction: "LONG" | "SHORT" | "FLAT";
  isConflict: boolean;
  contractType: "STOCK" | "OPTION";
  expiration: string;
  strike: number;
  right: string;
  contributions: Array<{
    productName: string;
    productKey: string;
    effectiveWeight: number;
  }>;
}

export interface UseStrategyHoldingsResult {
  holdings: EnrichedHoldingRow[];
  summary: StrategyHoldingsSummary;
  missingProducts: string[];
  isLoading: boolean;
  error: Error | null;
  retry: () => void;
}

export interface UseStrategyHoldingsOptions {
  // Alpha resolves product dependencies before this hook sees the leaf set.
  // Opt in there so a changed leaf set reports loading on its first render,
  // before this hook's fetch effect starts. Legacy callers keep the existing
  // first-render behavior by default.
  reportPendingBeforeEffect?: boolean;
}

export function useStrategyHoldings(
  strategyEntries: StrategyPositionInput[],
  productItems: ProductSearchItem[],
  basketLookup: Map<number, ProductSearchItem>,
  options: UseStrategyHoldingsOptions = {}
): UseStrategyHoldingsResult {
  const reportPendingBeforeEffect = options.reportPendingBeforeEffect ?? false;
  const [contractMap, setContractMap] = useState<Record<number, ContractDetails>>({});
  const [optionChainMap, setOptionChainMap] = useState<Map<number, OptionChainContract>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [startedLoadKey, setStartedLoadKey] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const flatResult = useMemo(
    () => flattenStrategyPositions(strategyEntries, productItems, basketLookup),
    [strategyEntries, productItems, basketLookup]
  );

  const flatPositions = flatResult.positions;
  const missingProducts = flatResult.missingProducts;

  const summary = useMemo(() => computeHoldingsSummary(flatPositions), [flatPositions]);

  const { stockIdKey, optionIdKey } = useMemo(() => {
    const stockIds: number[] = [];
    const optionIds: number[] = [];
    for (const p of flatPositions) {
      if (p.contractType === "OPTION") optionIds.push(p.conId);
      else stockIds.push(p.conId);
    }
    stockIds.sort((a, b) => a - b);
    optionIds.sort((a, b) => a - b);
    return {
      stockIdKey: stockIds.join(","),
      optionIdKey: optionIds.join(","),
    };
  }, [flatPositions]);

  const conIdKey = stockIdKey + (optionIdKey ? "|" + optionIdKey : "");
  const loadKey = `${conIdKey}|${retryCount}`;

  useEffect(() => {
    if (reportPendingBeforeEffect) setStartedLoadKey(loadKey);
    if (conIdKey === "") {
      ++fetchIdRef.current;
      setContractMap((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      setOptionChainMap((prev) => (prev.size === 0 ? prev : new Map()));
      setIsLoading(false);
      setError(null);
      return;
    }
    const allIds = conIdKey.replace("|", ",").split(",").filter(Boolean).map(Number);
    const optionIds = optionIdKey ? optionIdKey.split(",").map(Number) : [];
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);

    const contractsPromise = contractsService.getContractDetailsMap(allIds);
    const optionsPromise =
      optionIds.length > 0
        ? optionChainsV2Service
            .findOptionContractsByConIds(optionIds)
            .then((res) => res.optionContracts)
            .catch(() => [] as OptionChainContract[])
        : Promise.resolve([] as OptionChainContract[]);

    Promise.all([contractsPromise, optionsPromise]).then(
      ([cMap, optContracts]) => {
        if (fetchId !== fetchIdRef.current) return;
        setContractMap(cMap);
        const ocMap = new Map<number, OptionChainContract>();
        for (const oc of optContracts) ocMap.set(oc.conId, oc);
        setOptionChainMap(ocMap);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        if (fetchId !== fetchIdRef.current) return;
        setIsLoading(false);
        setError(err instanceof Error ? err : new Error("Failed to load contract data. Please retry."));
      }
    );
  }, [conIdKey, optionIdKey, retryCount, loadKey, reportPendingBeforeEffect]);

  const holdings = useMemo((): EnrichedHoldingRow[] => {
    return flatPositions.map((pos: StrategyFlatPosition): EnrichedHoldingRow => {
      const contract = contractMap[pos.conId];
      const oc = optionChainMap.get(pos.conId);
      const expiry = oc?.expiryDateLocal ?? "";
      const normalizedExpiry =
        expiry && /^\d{8}$/.test(expiry)
          ? `${expiry.slice(0, 4)}-${expiry.slice(4, 6)}-${expiry.slice(6, 8)}`
          : expiry;
      return {
        conId: pos.conId,
        ticker: contract?.symbol ?? oc?.symbol ?? `#${pos.conId}`,
        name: contract?.longName ?? contract?.symbol ?? oc?.symbol ?? "",
        exchange: contract?.primaryExchange ?? contract?.exchange ?? oc?.exchange ?? "",
        currency: contract?.currency ?? "USD",
        netWeight: pos.netWeight,
        direction: pos.netWeight > 0 ? "LONG" : pos.netWeight < 0 ? "SHORT" : "FLAT",
        isConflict: pos.isConflict,
        contractType: pos.contractType,
        expiration: normalizedExpiry,
        strike: oc?.strikePrice ?? 0,
        right: oc?.putCall ?? "",
        contributions: pos.contributions,
      };
    });
  }, [flatPositions, contractMap, optionChainMap]);

  const retry = () => setRetryCount((c) => c + 1);

  const isPendingBeforeEffect =
    reportPendingBeforeEffect && conIdKey !== "" && startedLoadKey !== loadKey;

  return {
    holdings,
    summary,
    missingProducts,
    isLoading: isPendingBeforeEffect || isLoading,
    error,
    retry,
  };
}
